import { Pagination } from "@core/domain/models/pagination";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { Category } from "@entity/domain/models/category.model";
import { Collective } from "@entity/domain/models/collective.model";
import {
    EntitiesSummary,
    type EntitySearchFilters,
} from "@entity/domain/models/entity-summary.model";
import { GetEntitiesByUseCase } from "@entity/domain/usecases/entity/get-entities-by.usecase";
import { GetAllCategoriesUseCase } from "@entity/domain/usecases/get-all-categories.usecase";
import { GetAllCollectivesUseCase } from "@entity/domain/usecases/get-all-collectives.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class EntityListPageViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    entities: EntitiesSummary = new EntitiesSummary([], 0);

    @observable
    categories: Category[] = [];

    @observable
    collectives: Collective[] = [];

    @observable
    pagination: Pagination = new Pagination();

    initialFiltersValue: EntitySearchFilters = {
        name: "",
        collectiveIds: [],
        categoryType: null,
        active: true,
    };

    @observable
    filters: EntitySearchFilters = this.initialFiltersValue;

    constructor(
        @inject(GetAllCollectivesUseCase)
        private readonly getAllCollectivesUsecase: GetAllCollectivesUseCase,
        @inject(GetAllCategoriesUseCase)
        private readonly getAllCategoriesUseCase: GetAllCategoriesUseCase,
        @inject(GetEntitiesByUseCase)
        private readonly getEntitiesByUseCase: GetEntitiesByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([
            this.searchEntitiesBy(this.filters, this.pagination),
            this.getAllCategories(),
            this.getAllCollectives(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setFilters(filters: EntitySearchFilters): void {
        this.filters = filters;
        this.pagination.reset();

        this.searchEntitiesBy(this.filters, this.pagination);
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;

        this.searchEntitiesBy(this.filters, this.pagination);
    }

    async searchEntitiesBy(
        filters: EntitySearchFilters,
        pagination: Pagination,
    ): Promise<void> {
        const entities = await this.getEntitiesByUseCase.execute(
            pagination,
            filters,
        );
        runInAction(() => {
            this.entities = entities;
        });
    }

    async getAllCategories(): Promise<void> {
        const categories = await this.getAllCategoriesUseCase.execute();

        runInAction(() => {
            this.categories = categories;
        });
    }

    async getAllCollectives(): Promise<void> {
        const collectives = await this.getAllCollectivesUsecase.execute();

        runInAction(() => {
            this.collectives = collectives;
        });
    }
}
